import type { TranscriptionFailureCode } from '@kiao-voice/core'
import type { AnonymousTrialResult } from './types'
import {
  deriveItemStageView,
  stageLabelKey,
  type ItemStageView,
} from '../batch-transcription/stage-progress'
import type { TrialUploadFailureReason } from './upload-failure-reason'

/**
 * 匿名試轉的狀態機與畫面模型，純函式。
 *
 * 抽出來的理由不是「分層比較漂亮」，是這條流程有三件事只有在純函式裡才驗得到：
 * 進度百分比**永不回退**、每一種失敗都對到一句話、以及上傳段與轉錄段是同一條
 * 進度線的兩半。這三件事混在 component 裡就只能靠人點一遍檔案上傳看看。
 *
 * 時間一律由參數傳入（`nowMs`），不在這裡讀時鐘：轉錄段的百分比會隨時間內插，
 * 讀時鐘的版本沒辦法寫「兩秒後應該變成幾」的測試。
 */

/**
 * 送出階段：準備、裁切、上傳、確認。狀態機的「進行中」狀態就是這四個，
 * 上傳端（`api-client.ts` 的 `onPhase`）回報的也是這四個。
 */
export const ANONYMOUS_SUBMIT_PHASES = ['preparing', 'trimming', 'uploading', 'finalizing'] as const
export type AnonymousSubmitPhase = (typeof ANONYMOUS_SUBMIT_PHASES)[number]

/** 畫面要顯示的檔案資訊。不留 `File` 本身——view 不需要它，測試也不必造一個。 */
export interface FileMeta {
  name: string
  sizeBytes: number
  durationSecs?: number | null
}

/**
 * 失敗原因。前端的副檔名預檢、伺服器對檔案本身的拒絕（讀不出時長、太大）、
 * 上傳失敗分類（{@link TrialUploadFailureReason}，與埋點共用同一組名字）、
 * 以及轉錄本身失敗，收在同一個聯集裡——對使用者來說它們都只是「這次沒成功，
 * 因為某個原因」。長度不是拒絕理由：試轉任何長度都收，只轉前 `trimSecs` 秒。
 */
export type TrialFailureKind =
  | 'unsupported_format'
  | 'duration_unreadable'
  | 'file_too_large'
  | TrialUploadFailureReason
  | 'transcription_failed'

export interface TrialFailure {
  kind: TrialFailureKind
  /** `file_too_large`：server extras 的上限（bytes）。 */
  maxBytes?: number
  /** `transcription_failed`：拿去對既有的 `transcriptionFailureMessage`。 */
  errorCode?: TranscriptionFailureCode | null
}

export type TrialState =
  | { kind: 'idle' }
  | { kind: 'preparing'; file: FileMeta }
  /** 瀏覽器正在把前幾分鐘的音軌切出來（見 `trim-first-minutes.ts`）。 */
  | { kind: 'trimming'; file: FileMeta }
  | { kind: 'uploading'; file: FileMeta; fraction: number }
  | { kind: 'finalizing'; file: FileMeta }
  | {
      kind: 'polling'
      file: FileMeta
      shortId: string
      result: AnonymousTrialResult | null
      /** 已經顯示過的最高百分比。見 {@link reduce} 的 `pollResult`。 */
      maxPercent: number
    }
  | { kind: 'done'; file: FileMeta; shortId: string; audioDurationSecs: number | null }
  | { kind: 'failed'; file: FileMeta | null; failure: TrialFailure }

export type TrialEvent =
  | { type: 'fileChosen'; file: FileMeta }
  | { type: 'phase'; phase: AnonymousSubmitPhase }
  | { type: 'uploadProgress'; fraction: number }
  | { type: 'submitted'; shortId: string }
  | { type: 'pollResult'; result: AnonymousTrialResult; nowMs: number }
  | { type: 'failed'; failure: TrialFailure }
  | { type: 'cancelled' }
  | { type: 'reset' }

const IN_FLIGHT: ReadonlySet<TrialState['kind']> = new Set(ANONYMOUS_SUBMIT_PHASES)

/** 拿得到檔案資訊的狀態——失敗時要把它接著顯示在錯誤面板上。 */
function fileOf(state: TrialState): FileMeta | null {
  return 'file' in state ? state.file : null
}

export const initialTrialState: TrialState = { kind: 'idle' }

export function reduce(state: TrialState, event: TrialEvent): TrialState {
  switch (event.type) {
    case 'fileChosen':
      return { kind: 'preparing', file: event.file }

    case 'phase': {
      const file = fileOf(state)
      // 已經送出（polling／done）之後才收到的階段回報是遲到的訊息，不是新狀態。
      if (file === null || !IN_FLIGHT.has(state.kind)) return state
      if (event.phase === 'uploading') {
        return { kind: 'uploading', file, fraction: state.kind === 'uploading' ? state.fraction : 0 }
      }
      return { kind: event.phase, file }
    }

    case 'uploadProgress': {
      if (state.kind !== 'uploading') return state
      const fraction = clampUnit(event.fraction)
      // 上傳的百分比也不回退：XHR 的 progress 事件在重試時會從頭數。
      return { ...state, fraction: Math.max(state.fraction, fraction) }
    }

    case 'submitted': {
      const file = fileOf(state)
      if (file === null) return state
      return { kind: 'polling', file, shortId: event.shortId, result: null, maxPercent: 0 }
    }

    case 'pollResult': {
      if (state.kind !== 'polling') return state
      const { result } = event
      if (result.status === 'completed') {
        return {
          kind: 'done',
          file: state.file,
          shortId: state.shortId,
          audioDurationSecs: result.audioDurationSecs,
        }
      }
      if (result.status === 'failed' || result.status === 'cancelled') {
        return {
          kind: 'failed',
          file: state.file,
          failure: { kind: 'transcription_failed', errorCode: result.errorCode },
        }
      }
      // 百分比的地板存在 state 裡，而不是留給畫面層自己記：它是這條流程的
      // 規則之一（線只能往前），寫在 reducer 裡才驗得到。
      const percent = stageViewOf(result, event.nowMs).percent ?? 0
      return {
        ...state,
        result,
        maxPercent: Math.max(state.maxPercent, percent),
      }
    }

    case 'failed':
      return { kind: 'failed', file: fileOf(state), failure: event.failure }

    case 'cancelled':
      // 使用者自己按取消不是失敗，直接回到閒置——錯誤面板會讓他以為出事了。
      return IN_FLIGHT.has(state.kind) ? { kind: 'idle' } : state

    case 'reset':
      return { kind: 'idle' }
  }
}

/** 把試轉結果轉成 batch 那一套的進度模型。欄位缺（舊版 API）就退化成不定進度。 */
function stageViewOf(result: AnonymousTrialResult, nowMs: number): ItemStageView {
  return deriveItemStageView(
    {
      status: result.stage ?? '',
      startedAt: result.startedAt ?? null,
      audioDurationSecs: result.audioDurationSecs,
      progress: result.progress ?? null,
    },
    nowMs
  )
}

export type TrialPanelKind = 'idle' | 'run' | 'error'

export interface TrialPanelView {
  kind: TrialPanelKind
  /** 只有這個值變的時候才更新畫面上的標題（螢幕閱讀器不會每兩秒念一次）。 */
  titleKey: string
  titleParams?: Record<string, string | number>
  /** 0–100，無條件捨去；null 代表沒有可信的數字。 */
  percent: number | null
  indeterminate: boolean
  done: boolean
  showCancel: boolean
  showSeeResult: boolean
  fileName: string | null
  fileMetaKey: string | null
  fileMetaParams?: Record<string, string | number>
  noteKey: string | null
  /**
   * 「上傳中請留在這個頁面」。只在上傳的三個階段出現（準備／上傳／處理中），
   * 因為那段有一條進行中的連線：iOS 把分頁丟到背景就會把它砍掉，回來時上傳已死。
   * 轉錄階段不顯示——那時沒有我們這邊的連線要保，離開只是停止輪詢，而且
   * `noteTranscribing` 已經說了可以留在這一頁。
   */
  holdKey: string | null
  resultHref?: string
  errorDescKey?: string
  errorDescParams?: Record<string, string | number>
}

const UPLOAD_TITLE: Record<'preparing' | 'uploading' | 'finalizing', string> = {
  preparing: 'transcriptions.progress.upload.preparingSingle',
  uploading: 'transcriptions.progress.upload.uploading',
  finalizing: 'transcriptions.progress.upload.finalizing',
}

/**
 * 失敗原因 → 一句說明。沿用 `batchTranscription.*` 既有的說法，同一件事只有一種講法。
 *
 * `rate_limited` 的文案不寫「今天」：伺服器端 `countRecent()` 是往回滾動 24 小時
 * （`anonymous_trial_service.ts`：`DateTime.now().minus({ hours: 24 })`），不是日曆天。
 */
const FAILURE_DESC: Record<TrialFailureKind, string> = {
  unsupported_format: 'batchTranscription.validationError.unsupportedFormat',
  duration_unreadable: 'batchTranscription.guestNoticeUnreadable',
  file_too_large: 'batchTranscription.guestNoticeTooBig',
  rate_limited: 'batchTranscription.guestNoticeRateLimitedRolling',
  trial_disabled: 'batchTranscription.guestNoticeDisabled',
  upload_stalled: 'batchTranscription.guestNoticeStalled',
  upload_not_found: 'batchTranscription.guestNoticeUploadFailedRetry',
  network: 'batchTranscription.guestNoticeUploadFailedRetry',
  server_error: 'batchTranscription.guestNoticeUploadFailedRetry',
  unexpected_response: 'batchTranscription.guestNoticeUploadFailedRetry',
  other: 'batchTranscription.guestNoticeUploadFailedRetry',
  // 轉錄失敗的說明由 `transcriptionFailureMessage(t, errorCode)` 產生，不走這張表。
  transcription_failed: '',
}

/** 上傳期間那句「請留在這個頁面」。 */
const HOLD_KEY = 'transcriptionPage.trial.stayOnPage'

/**
 * 免費試轉實際會轉幾秒。上傳框的文案用它，伺服器仍是權威——`direct/initiate`
 * 回的 `trimSecs` 才是這次真正會套用的值。
 */
export const TRIAL_TRIM_SECS_FALLBACK = 300

/** 單檔大小上限（MB），對應 `ANONYMOUS_TRIAL_DIRECT_MAX_FILE_BYTES`。 */
export const TRIAL_MAX_MB_FALLBACK = 500

export function deriveTrialPanelView(state: TrialState, nowMs: number): TrialPanelView {
  const base = {
    percent: null as number | null,
    indeterminate: false,
    done: false,
    showCancel: false,
    showSeeResult: false,
    fileName: null as string | null,
    fileMetaKey: null as string | null,
    noteKey: null as string | null,
    holdKey: null as string | null,
  }

  switch (state.kind) {
    case 'idle':
      return { ...base, kind: 'idle', titleKey: 'transcriptionPage.trial.idleTitle' }

    case 'preparing':
    case 'finalizing':
      return {
        ...base,
        ...fileFields(state.file),
        kind: 'run',
        titleKey: UPLOAD_TITLE[state.kind],
        percent: null,
        indeterminate: true,
        showCancel: state.kind === 'preparing',
        noteKey: 'transcriptionPage.trial.noteUploading',
        holdKey: HOLD_KEY,
      }

    case 'trimming':
      return {
        ...base,
        ...fileFields(state.file),
        kind: 'run',
        titleKey: 'transcriptionPage.trial.trimming',
        // 伺服器回的 `trimSecs` 才是權威，但這一行只是說明「正在做什麼」，
        // 而閒置畫面的規格說明用的也是同一個常數，兩處寫同一個數字。
        titleParams: { minutes: TRIAL_TRIM_SECS_FALLBACK / 60 },
        percent: null,
        indeterminate: true,
        showCancel: true,
        noteKey: 'transcriptionPage.trial.noteTrimming',
        // 裁切本身不怕切走，但它夾在 initiate 與 PUT 中間，而那一句在前後兩個
        // 階段都在；只有這一格拿掉，畫面上就會閃一下。
        holdKey: HOLD_KEY,
      }

    case 'uploading':
      return {
        ...base,
        ...fileFields(state.file),
        kind: 'run',
        titleKey: UPLOAD_TITLE.uploading,
        percent: Math.floor(state.fraction * 100),
        indeterminate: false,
        showCancel: true,
        noteKey: 'transcriptionPage.trial.noteUploading',
        holdKey: HOLD_KEY,
      }

    case 'polling': {
      const result = state.result
      // 還沒問到第一次結果，或還沒被 pipeline 認領：排隊是真的沒有在動，
      // 給一個數字會是編的。
      //
      // **判斷排隊要看 `stage` 而不是 `status`。** `status` 是試轉那一列
      // 自己的狀態，它從送出到轉完都維持 `pending`，只有終局才翻成
      // `completed`／`failed`；真正在走的位置在 `stage`（實測 API 回應：
      // status=pending 但 stage=transcribing、progress.subStage=stt）。
      // 用 `status` 當閘門會把整個轉錄過程都吞成「排隊中」。
      if (result === null || !result.stage || result.stage === 'pending') {
        return {
          ...base,
          ...fileFields(state.file),
          kind: 'run',
          titleKey: 'transcriptions.progress.submitted',
          percent: null,
          indeterminate: true,
          noteKey: 'transcriptionPage.trial.noteTranscribing',
        }
      }
      const view = stageViewOf(result, nowMs)
      const percent =
        view.percent === null ? null : Math.max(state.maxPercent, Math.floor(view.percent))
      return {
        ...base,
        ...fileFields(state.file),
        kind: 'run',
        titleKey: view.stageLabelKey,
        ...(view.subStageLabelKey ? { titleParams: { sub: view.subStageLabelKey } } : {}),
        percent,
        indeterminate: view.indeterminate,
        noteKey: 'transcriptionPage.trial.noteTranscribing',
      }
    }

    case 'done':
      return {
        ...base,
        ...fileFields({ ...state.file, durationSecs: state.audioDurationSecs }),
        kind: 'run',
        titleKey: stageLabelKey('done'),
        percent: 100,
        indeterminate: false,
        done: true,
        showSeeResult: true,
        resultHref: `/anonymous/result/${state.shortId}`,
        noteKey: 'transcriptionPage.trial.noteDone',
      }

    case 'failed':
      return {
        ...base,
        ...(state.file ? fileFields(state.file) : {}),
        kind: 'error',
        titleKey: failureTitleKey(state.failure),
        errorDescKey: FAILURE_DESC[state.failure.kind] || undefined,
        ...(failureDescParams(state.failure, state.file)
          ? { errorDescParams: failureDescParams(state.failure, state.file)! }
          : {}),
      }
  }
}

function fileFields(file: FileMeta) {
  return {
    fileName: file.name,
    fileMetaKey:
      typeof file.durationSecs === 'number' && file.durationSecs > 0
        ? 'transcriptionPage.trial.fileMetaWithDuration'
        : 'transcriptionPage.trial.fileMeta',
    fileMetaParams: {
      size: megabytes(file.sizeBytes),
      ...(typeof file.durationSecs === 'number' && file.durationSecs > 0
        ? {
            // 「約 1 分鐘」把 12 秒與 89 秒說成同一件事。分秒並列才對得上
            // 使用者手上的檔案。
            minutes: Math.floor(file.durationSecs / 60),
            seconds: Math.round(file.durationSecs % 60),
          }
        : {}),
    },
  }
}

function failureTitleKey(failure: TrialFailure): string {
  if (failure.kind === 'file_too_large') return 'transcriptionPage.trial.errorTooBig'
  if (failure.kind === 'rate_limited') return 'transcriptionPage.trial.errorRateLimited'
  if (failure.kind === 'trial_disabled') return 'transcriptionPage.trial.errorDisabled'
  if (failure.kind === 'unsupported_format') return 'transcriptionPage.trial.errorUnsupported'
  if (failure.kind === 'transcription_failed') return 'transcriptionPage.trial.errorTranscription'
  return 'transcriptionPage.trial.errorUploadFailed'
}

function failureDescParams(
  failure: TrialFailure,
  file: FileMeta | null
): Record<string, string | number> | null {
  if (failure.kind === 'unsupported_format' && file) return { filename: file.name }
  if (failure.kind === 'file_too_large') {
    return { maxMb: failure.maxBytes ? Math.floor(failure.maxBytes / (1024 * 1024)) : TRIAL_MAX_MB_FALLBACK }
  }
  return null
}

function megabytes(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(1)
}

function clampUnit(value: number): number {
  if (!Number.isFinite(value)) return 0
  return Math.min(1, Math.max(0, value))
}
